import type { JSX } from 'react'
import { useImportsSummary } from '../../hooks/useImports.ts'

export interface AsOfStampProps {
  computedAt?: string | null   // ISO; null → "just now"
  modelSyncedAt?: string | null
  amiAsOf?: string | null
}

/** ISO datetime → locale datetime; unparseable input renders verbatim. */
function fmtDateTime(ts: string): string {
  const d = new Date(ts)
  return isNaN(d.getTime()) ? ts : d.toLocaleString()
}

/** Date-only formatting for the synced/AMI segments. Plain YYYY-MM-DD strings
 *  render verbatim — new Date('2026-08-10') is UTC midnight, and
 *  toLocaleDateString() would shift it a day in Mountain time. */
function fmtDate(ts: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(ts)) return ts
  const d = new Date(ts)
  return isNaN(d.getTime()) ? ts : d.toLocaleDateString()
}

/** One shared as-of caption for every analysis (spec A6 / contract §3.4):
 *  `Computed <when> · model data synced <date> · AMI as of <date>`, segments
 *  omitted when null. When the caller does not pass `modelSyncedAt`, the GIS
 *  model sync date comes from the imports summary via useImportsSummary()
 *  (one dedup'd fetch app-wide); pass `modelSyncedAt={null}` to suppress the
 *  segment explicitly.
 *
 *  It reads `last_successful_run`, NOT `last_run`: `last_run` is the newest
 *  run whatever its outcome, and the nightly stamps `completed_at` even on the
 *  runs it marks failed (scripts/sync_gis.py), so `last_run.completed_at`
 *  would date the model to a sync that never landed — and a run in flight
 *  (null `completed_at`) would blank the segment instead of falling back to
 *  the last good sync. */
export function AsOfStamp(props: AsOfStampProps): JSX.Element {
  const { data: imports } = useImportsSummary()
  const modelSyncedAt =
    props.modelSyncedAt !== undefined
      ? props.modelSyncedAt
      : imports?.last_successful_run?.completed_at ?? null

  const segments: string[] = [
    `Computed ${props.computedAt ? fmtDateTime(props.computedAt) : 'just now'}`,
  ]
  if (modelSyncedAt) segments.push(`model data synced ${fmtDate(modelSyncedAt)}`)
  if (props.amiAsOf) segments.push(`AMI as of ${fmtDate(props.amiAsOf)}`)

  return <div className="text-[11px] text-gray-400">{segments.join(' · ')}</div>
}
